// src/lib/walkthrough/repositionTracking.ts
//
// Pure, DOM-free helpers for the walkthrough spotlight's LIVE-target tracking.
//
// Bug they guard (docs/REGRESSIONS.md): the guided-tour spotlight measured its
// target's bounding rect ONCE at step-resolve time and only re-measured on
// window 'resize'/'scroll'. On the contact-record (360) page, the async panels
// (Open Deals / Active Projects / Lifetime Won / Open Tickets / Leads / Projects
// counts + the AI record summary) finish loading AFTER the step resolves and
// reflow the layout WITHOUT firing resize or scroll — pushing the "+ add deal"
// icon (step 5/6 "Click this icon.", `[data-tour-id="deal-360-add"]`) down while
// the highlight stayed at the stale rect, landing over the empty "Leads (0)"
// area. WalkthroughPlayer now installs a ResizeObserver + MutationObserver + a
// short requestAnimationFrame settle loop that re-track the target through
// post-load reflow. These helpers are the framework-agnostic change-detection
// core of that loop: cheap to call every frame, and they only trigger a costly
// relayout when the target actually MOVED.

export type RectLike = { top: number; left: number; width: number; height: number }

// Stable, integer-rounded fingerprint of a rect. Equal positions ⇒ equal key,
// so the rAF settle loop can skip relayout when nothing moved (sub-pixel jitter
// is rounded away to avoid thrash).
export function rectKey(r: RectLike): string {
  return `${Math.round(r.top)}:${Math.round(r.left)}:${Math.round(r.width)}:${Math.round(r.height)}`
}

// True when `r` no longer matches the previously-tracked key — i.e. the target
// has moved/resized (the post-load reflow case) and the spotlight must be
// repositioned to follow it.
export function hasRectMoved(prevKey: string, r: RectLike): boolean {
  return rectKey(r) !== prevKey
}
